import { forwardRef, type Ref, type ReactNode } from 'react';
import { useStableId, useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import './SegmentedControl.css';

export interface SegmentedControlItem {
  label: ReactNode;
  value: string;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  data: Array<string | SegmentedControlItem>;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
}

function normalize(data: SegmentedControlProps['data']): SegmentedControlItem[] {
  return data.map((item) => (typeof item === 'string' ? { label: item, value: item } : item));
}

/** Segmented (tab-style) radio control. */
export const SegmentedControl = forwardRef(function SegmentedControl(
  { data, value, defaultValue, onChange, name, size = 'sm', fullWidth, disabled, className }: SegmentedControlProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const items = normalize(data);
  const [val, setVal] = useUncontrolled<string>({
    value, defaultValue: defaultValue ?? items[0]?.value, finalValue: items[0]?.value ?? '', onChange
  });
  const auto = useStableId('rr-segctl');
  const groupName = name ?? auto;
  return (
    <div
      ref={ref}
      role="radiogroup"
      className={['rr-segmented', `rr-segmented--size-${size}`, fullWidth && 'rr-segmented--full', className].filter(Boolean).join(' ')}
    >
      {items.map((item) => (
        <label key={item.value} className={`rr-segmented__item ${val === item.value ? 'rr-segmented__item--active' : ''}`}>
          <input
            type="radio"
            name={groupName}
            value={item.value}
            checked={val === item.value}
            disabled={disabled || item.disabled}
            onChange={() => setVal(item.value)}
          />
          <span>{item.label}</span>
        </label>
      ))}
    </div>
  );
});
